import { useMemo, useRef, useState } from "react";

function normalizeMinimumStock(value, fallback = 1) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(0, Math.floor(parsed));
}

function normalizeDataText(value) {
  return String(value ?? '')
    .toLowerCase()
    .replace(/[!?.,;:]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function safeAverage(total, count) {
  return count > 0 ? total / count : 0;
}

function toDateValue(value) {
  if (!value) return null;

  try {
    if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
    if (typeof value?.toDate === 'function') {
      const converted = value.toDate();
      return converted instanceof Date && !Number.isNaN(converted.getTime()) ? converted : null;
    }

    if (typeof value === 'number') {
      const converted = new Date(value);
      return Number.isNaN(converted.getTime()) ? null : converted;
    }

    if (typeof value === 'string') {
      const converted = new Date(value);
      return Number.isNaN(converted.getTime()) ? null : converted;
    }
  } catch {
    return null;
  }

  return null;
}

function toHoursValue(value) {
  if (value == null || value === '') return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return Math.max(0, value);

  const text = String(value).trim().toLowerCase().replace(',', '.');
  if (!text) return 0;

  if (/^\d{1,2}:\d{2}$/.test(text)) {
    const [hours, minutes] = text.split(':').map(Number);
    return (Number.isFinite(hours) ? hours : 0) + ((Number.isFinite(minutes) ? minutes : 0) / 60);
  }

  const numberMatch = text.match(/-?\d+(?:\.\d+)?/);
  if (!numberMatch) return 0;

  const numeric = Number(numberMatch[0]);
  if (!Number.isFinite(numeric)) return 0;

  if (text.includes('min')) return Math.max(0, numeric / 60);
  return Math.max(0, numeric);
}

function extractRobotLabel(melding = {}) {
  const candidates = [
    melding.robotNaam,
    melding.robotName,
    melding.naam,
    melding.robot,
    melding.maaier,
    melding.kNummer,
    melding.knummer
  ];

  const match = candidates.find((value) => String(value || '').trim());
  return match ? String(match).trim() : 'onbekende robot';
}

function extractVestigingLabel(melding = {}) {
  const candidates = [
    melding.gemeente,
    melding.vestiging,
    melding.branchName,
    melding.branchId,
    melding.userBranchId,
    melding.klant
  ];

  const match = candidates.find((value) => String(value || '').trim());
  return match ? String(match).trim() : 'onbekende vestiging';
}

function formatHoursForSpeech(hours) {
  if (!Number.isFinite(hours) || hours <= 0) return 'minder dan 5 minuten';
  const roundedMinutes = Math.round(hours * 60);
  const wholeHours = Math.floor(roundedMinutes / 60);
  const minutes = roundedMinutes % 60;

  if (wholeHours <= 0) return `${minutes} minuten`;
  if (minutes <= 0) return `${wholeHours} uur`;
  return `${wholeHours} uur en ${minutes} minuten`;
}

function extractHistoricalWeatherDate(text) {
  const normalized = normalizeDataText(text);
  const date = new Date();
  date.setHours(12, 0, 0, 0);

  if (normalized.includes('eergisteren')) {
    date.setDate(date.getDate() - 2);
    return date;
  }

  if (normalized.includes('gisteren')) {
    date.setDate(date.getDate() - 1);
    return date;
  }

  if (normalized.includes('vorige week')) {
    date.setDate(date.getDate() - 7);
    return date;
  }

  const explicitDate = normalized.match(/\b(\d{1,2})[-/](\d{1,2})(?:[-/](\d{2,4}))?\b/);
  if (explicitDate) {
    const day = Number(explicitDate[1]);
    const month = Number(explicitDate[2]) - 1;
    const year = explicitDate[3] ? Number(explicitDate[3].length === 2 ? `20${explicitDate[3]}` : explicitDate[3]) : new Date().getFullYear();
    const parsed = new Date(year, month, day, 12, 0, 0, 0);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}

function extractRequestedRobot(text, robotNames = []) {
  const normalized = normalizeDataText(text);
  const candidates = (Array.isArray(robotNames) ? robotNames : [])
    .map((name) => ({ name, normalized: normalizeDataText(name) }))
    .filter((item) => item.normalized && item.normalized !== 'onbekende robot')
    .sort((a, b) => b.normalized.length - a.normalized.length);

  const directMatch = candidates.find((item) => normalized.includes(item.normalized));
  if (directMatch) return directMatch.name;

  const robotPhrase = normalized.match(/robot\s+([a-z0-9\- ]{2,})/);
  return robotPhrase ? robotPhrase[1].trim() : '';
}

function buildMeldingVoiceInsights({ meldingen = [], filteredMeldingen = [], onderdelen = [], magazijnVoorraad = [] }) {
  const allMeldingen = Array.isArray(meldingen) ? meldingen : [];
  const visibleMeldingen = Array.isArray(filteredMeldingen) ? filteredMeldingen : allMeldingen;
  const robotMap = new Map();
  const vestigingMap = new Map();

  let workedTotal = 0;
  let workedCount = 0;
  let travelTotal = 0;
  let travelCount = 0;
  let urgentCount = 0;
  let todayCount = 0;
  let weekCount = 0;

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const weekStart = new Date(todayStart);
  weekStart.setDate(weekStart.getDate() - 7);

  allMeldingen.forEach((melding) => {
    const workedHours = toHoursValue(
      melding?.werkTijd
      ?? melding?.werktijd
      ?? melding?.workedHours
      ?? melding?.workHours
      ?? melding?.uren
    );

    const travelHours = toHoursValue(
      melding?.reisTijd
      ?? melding?.reistijd
      ?? melding?.travelHours
      ?? melding?.travelTime
    );

    if (workedHours > 0) {
      workedTotal += workedHours;
      workedCount += 1;
    }

    if (travelHours > 0) {
      travelTotal += travelHours;
      travelCount += 1;
    }

    const createdAt = toDateValue(
      melding?.createdAt
      ?? melding?.datum
      ?? melding?.date
      ?? melding?.timestamp
    );

    if (createdAt) {
      if (createdAt >= todayStart) todayCount += 1;
      if (createdAt >= weekStart) weekCount += 1;
    }

    const priorityText = normalizeDataText(melding?.priority || melding?.urgentie || melding?.severity || '');
    if (/(hoog|urgent|kritiek|critical|spoed)/.test(priorityText)) {
      urgentCount += 1;
    }

    const robot = extractRobotLabel(melding);
    const robotEntry = robotMap.get(robot) || { name: robot, count: 0, workedTotal: 0, travelTotal: 0, lastStatus: 'open' };
    robotEntry.count += 1;
    robotEntry.workedTotal += workedHours;
    robotEntry.travelTotal += travelHours;
    robotEntry.lastStatus = status;
    robotMap.set(robot, robotEntry);

    const vestiging = extractVestigingLabel(melding);
    const vestigingEntry = vestigingMap.get(vestiging) || { name: vestiging, count: 0 };
    vestigingEntry.count += 1;
    vestigingMap.set(vestiging, vestigingEntry);
  });

  const robotInsights = Array.from(robotMap.values())
    .filter((item) => item.name !== 'onbekende robot' || robotMap.size === 1)
    .map((item) => ({
      ...item,
      averageWorkedHours: safeAverage(item.workedTotal, item.count),
      averageTravelHours: safeAverage(item.travelTotal, item.count),
    }))
    .sort((a, b) => b.count - a.count);

  const vestigingInsights = Array.from(vestigingMap.values())
    .filter((item) => item.name !== 'onbekende vestiging' || vestigingMap.size === 1)
    .sort((a, b) => b.count - a.count);

  const outOfStockCount = (Array.isArray(onderdelen) ? onderdelen : []).filter((item) => {
    const code = item?.code || item?.id;
    return (magazijnVoorraad.find((voorraad) => voorraad.id === code)?.aantal ?? 0) === 0;
  }).length;

  const lowStockCount = (Array.isArray(onderdelen) ? onderdelen : []).filter((item) => {
    const code = item?.code || item?.id;
    const voorraad = magazijnVoorraad.find((entry) => entry.id === code)?.aantal ?? 0;
    return voorraad > 0 && voorraad <= normalizeMinimumStock(item.minimumStock, 1);
  }).length;

  return {
    total: allMeldingen.length,
    visible: visibleMeldingen.length,
    todayCount,
    weekCount,
    urgentCount,
    averageWorkedHours: safeAverage(workedTotal, workedCount),
    averageTravelHours: safeAverage(travelTotal, travelCount),
    topRobot: robotInsights[0] || null,
    topVestiging: vestigingInsights[0] || null,
    robotInsights,
    vestigingInsights,
    outOfStockCount,
    lowStockCount,
  };
}

export default function useAppVoiceAssistant({
  user,
  isAdmin = false,
  isSuperAdmin = false,
  locationPath = '/',
  navigate,
  showToast,
  meldingen = [],
  filteredMeldingen = [],
  onderdelen = [],
  magazijnVoorraad = [],
  userBranchId = null,
  weekTechnician = null,
  getDisplayName = (value) => String(value || 'onbekend'),
  nativeAndroidVersion = '',
  currentAndroidVersion = 'dev',
  isOnline = true,
  androidUpdateInfo = null,
  fetchAndroidUpdate,
  downloadLatestAndroidApk,
  notificationCount = 0,
  notificationsEnabled = true,
  toggleNotifications,
  darkMode = false,
  toggleDarkMode,
  handleDetectRobot,
  startTravel,
  stopTravel,
}) {
  const [voiceSystem, setVoiceSystem] = useState({
    isListening: false,
    transcript: '',
    confidence: 0,
    response: '',
    voiceSupported: typeof window !== 'undefined' && ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window)
  });

  const lastVoiceExecutionRef = useRef({ intent: '', transcript: '', at: 0 });
  const pendingVoiceQuestionRef = useRef({ type: '', expiresAt: 0 });
  const weatherSummaryCacheRef = useRef({ at: 0, summary: '', key: 'current' });
  const voiceLearningRef = useRef({ total: 0, success: 0, phrases: {} });

  const operationsSnapshot = useMemo(
    () => buildMeldingVoiceInsights({ meldingen, filteredMeldingen, onderdelen, magazijnVoorraad }),
    [meldingen, filteredMeldingen, onderdelen, magazijnVoorraad]
  );

  const getVoiceLearningKey = () => `rbh_voice_assistant_profile_${user?.uid || 'guest'}`;

  const updateVoiceLearning = (transcript, intentKey = 'conversation') => {
    try {
      const key = getVoiceLearningKey();
      const saved = JSON.parse(localStorage.getItem(key) || '{}');
      const normalized = normalizeVoiceText(transcript);
      const next = {
        total: Number(saved.total || 0) + 1,
        success: Number(saved.success || 0) + 1,
        lastIntent: intentKey,
        lastTranscript: normalized,
        updatedAt: Date.now(),
        phrases: {
          ...(saved.phrases || {}),
          [intentKey]: {
            count: Number(saved?.phrases?.[intentKey]?.count || 0) + 1,
            lastExample: normalized,
          }
        }
      };
      voiceLearningRef.current = next;
      localStorage.setItem(key, JSON.stringify(next));
    } catch {
      void 0;
    }
  };

  const setVoiceResponse = (text) => {
    setVoiceSystem((prev) => ({ ...prev, response: String(text || '').trim() }));
  };

  const speakVoiceAssistant = (text) => {
    try {
      const spokenText = String(text || '').trim();
      if (!spokenText || typeof window === 'undefined' || !('speechSynthesis' in window)) return;
      setVoiceResponse(spokenText);
      const utterance = new SpeechSynthesisUtterance(spokenText);
      utterance.lang = 'nl-NL';
      utterance.rate = 0.95;
      utterance.pitch = 1;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utterance);
    } catch {
      void 0;
    }
  };

  const respondToVoiceAssistant = (message, type = 'info', options = {}) => {
    const text = String(message || '').trim();
    if (!text) return;
    const { speak = true, toast = true } = options;
    setVoiceResponse(text);
    if (toast && typeof showToast === 'function') showToast(text, type);
    if (speak) speakVoiceAssistant(text);
  };

  const normalizeVoiceText = (value) => String(value || '')
    .toLowerCase()
    .replace(/[!?.,;:]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  const stripWakeWord = (value) => {
    const normalized = normalizeVoiceText(value);
    const wakePattern = /^(hey|hé|hoi|oke|oké)\s+(robot|assistent|voice assistant|voice assistent)\b/;
    const directPattern = /^(robot|assistent|voice assistant|voice assistent)\b/;
    const withoutWake = normalized.replace(wakePattern, '').replace(directPattern, '').trim();

    return {
      wakeWordUsed: wakePattern.test(normalized) || directPattern.test(normalized),
      wakeOnly: normalized.length > 0 && withoutWake.length === 0,
      text: withoutWake || normalized,
    };
  };

  const getCurrentPageVoiceInfo = (path = locationPath) => {
    const isManager = isAdmin || isSuperAdmin;
    const pages = [
      {
        match: (value) => value.startsWith('/meldingen'),
        name: 'de meldingenpagina',
        summary: 'Hier maak je nieuwe robotmeldingen aan.',
        details: 'Op deze pagina kun je een nieuwe melding invullen, de weektechnicus zien en direct een robotprobleem registreren.'
      },
      {
        match: (value) => value.startsWith('/analytics/robots') || value.startsWith('/ana/robots'),
        name: 'robots analytics',
        summary: 'Hier zie je rapportages voor robots en prestaties.',
        details: 'Je kunt hier trends, totalen en analyses van robotmeldingen bekijken.'
      },
      {
        match: (value) => value.startsWith('/analytics/meldingen') || value.startsWith('/ana/meldingen'),
        name: 'melding analytics',
        summary: 'Hier zie je alle meldingen met uitgebreide filters.',
        details: 'Je kunt hier alle meldingen filteren op periode, robot, technicus, status en bijzonderheden.'
      },
      {
        match: (value) => value.startsWith('/analytics/onderdelen') || value.startsWith('/ana/onderdelen'),
        name: 'onderdelen analytics',
        summary: 'Hier zie je verbruik en voorraadinzichten voor onderdelen.',
        details: 'Je kunt hier onderdelenverbruik, prognoses en voorraadinformatie analyseren.'
      },
      {
        match: (value) => value.startsWith('/scoreboard'),
        name: 'het scoreboard',
        summary: 'Hier zie je prestaties en rankings.',
        details: 'Je kunt hier het klassement en prestatie-overzicht bekijken.'
      },
      {
        match: (value) => value.startsWith('/technician'),
        name: 'de technicuskaart',
        summary: 'Hier zie je locatie- en kaartinformatie.',
        details: 'Je kunt hier de technicuskaart en locatiegerelateerde informatie bekijken.'
      },
      {
        match: (value) => value.startsWith('/profile'),
        name: 'jouw profiel',
        summary: 'Hier beheer je instellingen, taal, updates en meldingsopties.',
        details: 'Je kunt hier profielinstellingen, notificaties, biometrie, app-updates en APK-download beheren.'
      },
      {
        match: (value) => value.startsWith('/bijzonderheden'),
        name: 'bijzonderheden',
        summary: 'Hier beheer je notities en aandachtspunten.',
        details: 'Je kunt hier bijzonderheden en relevante aandachtspunten voor robots of vestigingen bekijken.'
      },
      {
        match: (value) => value.startsWith('/administratie'),
        name: 'de administratie',
        summary: isManager ? 'Hier beheer je gebruikers, robots, garanties, documentatie en checklists.' : 'Deze sectie is vooral bedoeld voor beheerders.',
        details: isManager
          ? 'Binnen de administratie kun je meldingen, robots, dossiers, onderdelen, gebruikers, vestigingen, changelog, auditlog, portals en checklists beheren.'
          : 'Deze sectie bevat beheerfuncties en vereist adminrechten voor volledige toegang.'
      },
      {
        match: (value) => value.startsWith('/robots'),
        name: 'het robotbeheer',
        summary: 'Hier zie je robotbeheer en robotinformatie.',
        details: 'Op deze pagina kun je robots beheren en robotgegevens bekijken.'
      }
    ];

    return pages.find((page) => page.match(path)) || {
      name: 'de app',
      summary: 'Je zit in de Krinkels Robot Beheer app.',
      details: 'Ik kan je helpen met navigeren, statusinformatie geven en vragen over de app beantwoorden.'
    };
  };

  const getVoiceAssistantHelpText = () => (
    'Ik ben je centrale voice assistent voor de hele app. Zeg bijvoorbeeld hé assistent, open meldingen, open administratie gebruikers, hoe is het weer, hoeveel meldingen zijn er, welke vestiging heb ik, controleer update of zet donkere modus aan.'
  );

  const getCurrentPositionForWeather = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocatie niet beschikbaar'));
      return;
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: false,
      timeout: 10000,
      maximumAge: 10 * 60 * 1000
    });
  });

  const openOnlineSearch = (query) => {
    const normalizedQuery = String(query || '').trim();
    if (!normalizedQuery || typeof window === 'undefined') return false;

    try {
      const searchUrl = `https://duckduckgo.com/?q=${encodeURIComponent(normalizedQuery)}`;
      window.open(searchUrl, '_blank', 'noopener,noreferrer');
      return true;
    } catch {
      return false;
    }
  };

  const getOperationalBriefing = () => {
    const snapshot = operationsSnapshot;
    const robotPart = snapshot.topRobot
      ? `De drukste robot is nu ${snapshot.topRobot.name} met ${snapshot.topRobot.count} meldingen.`
      : 'Ik zie nog geen dominante robot in de huidige dataset.';

    const vestigingPart = snapshot.topVestiging
      ? `De drukste vestiging lijkt ${snapshot.topVestiging.name} met ${snapshot.topVestiging.count} meldingen.`
      : 'Ik zie nog geen duidelijke vestigingstrend.';

    return `Ik zie ${snapshot.total} meldingen. Meldingen hebben geen workflowstatus. Vandaag zijn er ${snapshot.todayCount} meldingen geregistreerd en in de laatste 7 dagen ${snapshot.weekCount}. ${robotPart} ${vestigingPart}`;
  };

  const findRobotPerformanceInsight = (queryText) => {
    const requestedRobot = extractRequestedRobot(queryText, operationsSnapshot.robotInsights.map((item) => item.name));
    if (!requestedRobot) {
      return `Gemiddeld zijn we ${formatHoursForSpeech(operationsSnapshot.averageWorkedHours)} bezig per robotbezoek, met gemiddeld ${formatHoursForSpeech(operationsSnapshot.averageTravelHours)} reistijd.`;
    }

    const robotData = operationsSnapshot.robotInsights.find(
      (item) => normalizeDataText(item.name) === normalizeDataText(requestedRobot)
    );

    if (!robotData) {
      return `Ik heb nu geen duidelijke historische data gevonden voor robot ${requestedRobot}.`;
    }

    return `Voor robot ${robotData.name} zie ik ${robotData.count} meldingen. Gemiddeld zijn we ${formatHoursForSpeech(robotData.averageWorkedHours)} bezig en de gemiddelde reistijd is ${formatHoursForSpeech(robotData.averageTravelHours)}.`;
  };

  const fetchWeatherSummary = async (queryText = '') => {
    const requestedDate = extractHistoricalWeatherDate(queryText);
    const cache = weatherSummaryCacheRef.current;
    const cacheKey = requestedDate ? requestedDate.toISOString().slice(0, 10) : 'current';

    if (cache.summary && cache.key === cacheKey && (Date.now() - cache.at) < 5 * 60 * 1000) {
      return cache.summary;
    }

    if (!navigator.onLine) {
      return 'Ik kan het weer nu niet ophalen omdat de app offline is.';
    }

    const weatherCodeLabels = {
      0: 'helder', 1: 'overwegend zonnig', 2: 'half bewolkt', 3: 'bewolkt',
      45: 'mistig', 48: 'mistig', 51: 'lichte motregen', 53: 'motregen', 55: 'stevige motregen',
      61: 'lichte regen', 63: 'regen', 65: 'hevige regen',
      71: 'lichte sneeuw', 73: 'sneeuw', 75: 'hevige sneeuw',
      80: 'plaatselijke regenbuien', 81: 'regenbuien', 82: 'hevige regenbuien', 95: 'onweer'
    };

    let latitude = 52.0907;
    let longitude = 5.1214;
    let exactLocation = false;

    try {
      const position = await getCurrentPositionForWeather();
      latitude = Number(position?.coords?.latitude || latitude);
      longitude = Number(position?.coords?.longitude || longitude);
      exactLocation = true;
    } catch {
      exactLocation = false;
    }

    try {
      if (requestedDate) {
        const isoDate = requestedDate.toISOString().slice(0, 10);
        const response = await fetch(
          `https://archive-api.open-meteo.com/v1/archive?latitude=${latitude}&longitude=${longitude}&start_date=${isoDate}&end_date=${isoDate}&daily=weather_code,temperature_2m_max,temperature_2m_min&timezone=auto`,
          { cache: 'no-store' }
        );

        if (!response.ok) throw new Error(`Historisch weer ophalen mislukt (${response.status})`);
        const payload = await response.json();
        const maxTemp = Math.round(Number(payload?.daily?.temperature_2m_max?.[0] || 0));
        const minTemp = Math.round(Number(payload?.daily?.temperature_2m_min?.[0] || 0));
        const weatherLabel = weatherCodeLabels[payload?.daily?.weather_code?.[0]] || 'wisselvallig';
        const dateLabel = new Intl.DateTimeFormat('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' }).format(requestedDate);

        const summary = `Voor ${dateLabel} zie ik historisch weer met ${weatherLabel}, ongeveer ${minTemp} tot ${maxTemp} graden.`;
        weatherSummaryCacheRef.current = { at: Date.now(), summary, key: cacheKey };
        return summary;
      }

      const url = `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,apparent_temperature,weather_code,wind_speed_10m&timezone=auto`;
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Weer ophalen mislukt (${response.status})`);

      const payload = await response.json();
      const current = payload?.current || {};
      const temperature = Math.round(Number(current.temperature_2m || 0));
      const apparent = Math.round(Number(current.apparent_temperature || temperature));
      const wind = Math.round(Number(current.wind_speed_10m || 0));
      const weatherLabel = weatherCodeLabels[current.weather_code] || 'wisselvallig';

      const summary = exactLocation
        ? `Het is nu ongeveer ${temperature} graden, het voelt als ${apparent} graden en het weer is ${weatherLabel}. De wind is ongeveer ${wind} kilometer per uur.`
        : `Ik kon je exacte locatie niet bepalen, maar op basis van de standaardlocatie is het nu ongeveer ${temperature} graden en ${weatherLabel}.`;

      weatherSummaryCacheRef.current = { at: Date.now(), summary, key: cacheKey };
      return summary;
    } catch {
      return 'Ik kon het weer op dit moment niet ophalen. Probeer het zo nog eens.';
    }
  };

  const getVoiceIntent = (transcript) => {
    const text = normalizeVoiceText(transcript);
    if (!text) return null;

    const robotManagementRoute = (isAdmin || isSuperAdmin) ? '/administratie/robots' : '/robots';

    const intents = [
      { key: 'meldingen', route: '/meldingen', toast: '✅ Ik open meldingen', phrases: ['nieuwe melding', 'melding', 'melding maken', 'robot melding', 'formulier'] },
      { key: 'analytics_robots', route: '/analytics/robots', toast: '✅ Ik open analytics voor robots', phrases: ['analytics robots', 'robot analytics', 'robots analyse', 'robot rapportage'] },
      { key: 'analytics_meldingen', route: '/analytics/meldingen', toast: '✅ Ik open melding analytics', phrases: ['melding analytics', 'meldingen analytics', 'alle meldingen analyse'] },
      { key: 'analytics_onderdelen', route: '/analytics/onderdelen', toast: '✅ Ik open analytics voor onderdelen', phrases: ['analytics onderdelen', 'onderdelen analytics', 'onderdelen analyse', 'voorraad analyse'] },
      { key: 'analytics', route: '/analytics/robots', toast: '✅ Ik open analytics', phrases: ['analytics', 'analyse', 'overzicht', 'rapportage'] },
      { key: 'scoreboard', route: '/scoreboard', toast: '✅ Ik open het scoreboard', phrases: ['scoreboard', 'prestaties', 'ranking', 'klassement'] },
      { key: 'map', route: '/technician', toast: '✅ Ik open de technicuskaart', phrases: ['kaart', 'map', 'technician', 'locatie', 'technicuskaart'] },
      { key: 'profiel', route: '/profile', toast: '✅ Ik open je profiel', phrases: ['profiel', 'instellingen', 'account pagina', 'mijn profiel'] },
      { key: 'bijzonderheden', route: '/bijzonderheden', toast: '✅ Ik open bijzonderheden', phrases: ['bijzonderheden', 'notities', 'memo', 'aandachtspunten'] },
      { key: 'robots_page', route: robotManagementRoute, toast: '✅ Ik open robots', phrases: ['open robots', 'ga naar robots', 'robotbeheer', 'robot management'] },
      { key: 'administratie', route: '/administratie', toast: '✅ Ik open de administratie', phrases: ['administratie', 'admin', 'beheer'] },
      { key: 'admin_meldingen', route: '/administratie/meldingen', toast: '✅ Ik open administratie meldingen', phrases: ['administratie meldingen', 'beheer meldingen', 'admin meldingen'] },
      { key: 'admin_robot_dossiers', route: '/administratie/robot-dossiers', toast: '✅ Ik open robot dossiers', phrases: ['robot dossier', 'robot dossiers', 'dossiers'] },
      { key: 'admin_onderdelen', route: '/administratie/onderdelen', toast: '✅ Ik open onderdelenbeheer', phrases: ['onderdelen beheer', 'admin onderdelen', 'beheer onderdelen'] },
      { key: 'admin_gebruikers', route: '/administratie/gebruikers', toast: '✅ Ik open gebruikersbeheer', phrases: ['gebruikers', 'gebruikers beheer', 'user beheer'] },
      { key: 'admin_vestigingen', route: '/administratie/vestigingen', toast: '✅ Ik open vestigingen', phrases: ['vestigingen', 'branches', 'branch beheer'] },
      { key: 'admin_garanties', route: '/administratie/garanties', toast: '✅ Ik open garanties', phrases: ['garanties', 'garantie beheer'] },
      { key: 'admin_documentatie', route: '/administratie/documentatie', toast: '✅ Ik open documentatie', phrases: ['documentatie', 'docs'] },
      { key: 'admin_changelog', route: '/administratie/changelog', toast: '✅ Ik open de changelog', phrases: ['changelog', 'wijzigingen'] },
      { key: 'admin_auditlog', route: '/administratie/auditlog', toast: '✅ Ik open de auditlog', phrases: ['auditlog', 'audit log', 'audit'] },
      { key: 'admin_portals', route: '/administratie/portals', toast: '✅ Ik open portals', phrases: ['portals', 'portal links', 'yamabiko', 'belrobotics'] },
      { key: 'admin_checklists', route: '/administratie/check-lists', toast: '✅ Ik open checklists', phrases: ['checklists', 'check lists', 'winterbeurt'] }
    ];

    let best = null;
    intents.forEach((intent) => {
      const score = intent.phrases.reduce((acc, phrase) => {
        if (text === phrase) return acc + 4;
        if (text.includes(phrase)) return acc + 2;
        const overlap = phrase.split(' ').filter((token) => token && text.includes(token)).length;
        return acc + (overlap > 0 ? 1 : 0);
      }, 0);

      if (!best || score > best.score) {
        best = { ...intent, score };
      }
    });

    if (!best || best.score <= 0) return null;
    return best;
  };

  const resolveAnalyticsFollowup = (text) => {
    const t = normalizeVoiceText(text);
    if (!t) return null;
    if (t.includes('onderdelen') || t.includes('voorraad') || t.includes('parts')) return '/analytics/onderdelen';
    if (t.includes('meldingen')) return '/analytics/meldingen';
    if (t.includes('robots') || t.includes('robot')) return '/analytics/robots';
    return null;
  };

  const resolveAdminFollowup = (text) => {
    const t = normalizeVoiceText(text);
    if (!t) return null;
    const targets = [
      { route: '/administratie/meldingen', tokens: ['melding', 'meldingen'] },
      { route: '/administratie/robot-dossiers', tokens: ['robot dossier', 'robot dossiers', 'dossier', 'dossiers'] },
      { route: '/administratie/robots', tokens: ['robot', 'robots'] },
      { route: '/administratie/onderdelen', tokens: ['onderdeel', 'onderdelen', 'voorraad'] },
      { route: '/administratie/gebruikers', tokens: ['gebruiker', 'gebruikers', 'users'] },
      { route: '/administratie/vestigingen', tokens: ['vestiging', 'vestigingen', 'branches'] },
      { route: '/administratie/garanties', tokens: ['garantie', 'garanties'] },
      { route: '/administratie/documentatie', tokens: ['documentatie', 'docs'] },
      { route: '/administratie/changelog', tokens: ['changelog', 'wijzigingen'] },
      { route: '/administratie/auditlog', tokens: ['audit', 'auditlog', 'log'] },
      { route: '/administratie/portals', tokens: ['portal', 'portals', 'links', 'belrobotics', 'yamabiko', 'intranet'] },
      { route: '/administratie/check-lists', tokens: ['checklist', 'checklists', 'check lists', 'winterbeurt'] }
    ];
    for (const target of targets) {
      if (target.tokens.some((token) => t.includes(token))) {
        return target.route;
      }
    }
    return null;
  };

  const handleVoiceQuestion = async (transcript) => {
    const wakeState = stripWakeWord(transcript);
    const text = wakeState.text;
    if (!text) return false;

    if (wakeState.wakeOnly) {
      respondToVoiceAssistant('Ja, ik luister. Vraag me bijvoorbeeld om een pagina te openen of naar de appstatus.', 'info');
      return true;
    }

    const pageInfo = getCurrentPageVoiceInfo(locationPath);
    const snapshot = operationsSnapshot;
    const outOfStockCount = snapshot.outOfStockCount;
    const lowStockCount = snapshot.lowStockCount;

    if (/^(hallo|hoi|hey|goedemorgen|goedemiddag|goedenavond)\b/.test(text)) {
      respondToVoiceAssistant('Hallo! Ik ben klaar om je door de app te helpen.', 'info');
      return true;
    }

    if (/(bedankt|dank je|thanks)/.test(text)) {
      respondToVoiceAssistant('Graag gedaan.', 'success');
      return true;
    }

    if (/wat kan je|wat kun je|help|hulp|welke commando/.test(text)) {
      respondToVoiceAssistant(getVoiceAssistantHelpText(), 'info');
      return true;
    }

    if (/(detecteer|scan|zoek).*(robot)/.test(text) && typeof handleDetectRobot === 'function') {
      await handleDetectRobot();
      respondToVoiceAssistant('Ik start de robotdetectie voor je.', 'success');
      return true;
    }

    if (/(start|begin).*(reis|travel|rit)/.test(text) && typeof startTravel === 'function') {
      await startTravel();
      respondToVoiceAssistant('Ik heb de reistijd gestart.', 'success');
      return true;
    }

    if (/(stop|einde|beëindig).*(reis|travel|rit)/.test(text) && typeof stopTravel === 'function') {
      await stopTravel();
      respondToVoiceAssistant('Ik heb de reistijd gestopt.', 'success');
      return true;
    }

    if (/wat onthoud je|voice profiel|spraak profiel|hoe leer je/.test(text)) {
      const learning = voiceLearningRef.current || {};
      respondToVoiceAssistant(`Ik heb tot nu toe ongeveer ${learning.total || 0} succesvolle spraakacties verwerkt en leer vooral van je meest gebruikte commando’s.`, 'info');
      return true;
    }

    if (/wat doet deze app|waarvoor is deze app/.test(text)) {
      respondToVoiceAssistant('Deze app is voor robotbeheer, meldingen, analytics, onderdelen, garanties, profielinstellingen en administratie.', 'info');
      return true;
    }

    if (/wat is krinkels|wie is krinkels/.test(text)) {
      respondToVoiceAssistant('Krinkels is hier de operationele context van het robotbeheer. Ik kan je helpen met meldingen, onderhoud, reistijden, onderdelen en administratie-inzichten.', 'info');
      return true;
    }

    if (/waar ben ik|welke pagina|welk scherm/.test(text)) {
      respondToVoiceAssistant(`Je zit nu op ${pageInfo.name}. ${pageInfo.summary}`, 'info');
      return true;
    }

    if (/wat kan ik hier|uitleg pagina|leg uit wat ik hier kan/.test(text)) {
      respondToVoiceAssistant(pageInfo.details, 'info');
      return true;
    }

    if (/hoe laat|tijd is het|wat is de tijd/.test(text)) {
      const currentTime = new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit' }).format(new Date());
      respondToVoiceAssistant(`Het is nu ${currentTime}.`, 'info');
      return true;
    }

    if (/welke datum|wat is de datum|welke dag is het vandaag|vandaag/.test(text) && !/weer/.test(text)) {
      const currentDate = new Intl.DateTimeFormat('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(new Date());
      respondToVoiceAssistant(`Vandaag is het ${currentDate}.`, 'info');
      return true;
    }

    if (/hoe is het weer|weer|temperatuur|regen|wind/.test(text)) {
      const summary = await fetchWeatherSummary(text);
      respondToVoiceAssistant(summary, 'info');
      return true;
    }

    if (/wie ben ik|wie is ingelogd|welk account gebruik ik/.test(text)) {
      const userName = user?.displayName || user?.email || 'onbekende gebruiker';
      respondToVoiceAssistant(`Je bent ingelogd als ${userName}.`, 'info');
      return true;
    }

    if (/mijn vestiging|welke vestiging|branch/.test(text)) {
      respondToVoiceAssistant(`Jouw vestiging is ${userBranchId || 'nog niet ingesteld'}.`, 'info');
      return true;
    }

    if (/weektechnicus|week technician|wie is de weektechnicus/.test(text)) {
      const technicianName = weekTechnician ? getDisplayName(weekTechnician) : 'nog niet ingesteld';
      respondToVoiceAssistant(`De weektechnicus is ${technicianName}.`, 'info');
      return true;
    }

    if (/statusoverzicht|status overzicht|dagoverzicht|operatieoverzicht|hoe staan we ervoor|hoe gaat het vandaag/.test(text)) {
      respondToVoiceAssistant(getOperationalBriefing(), 'info');
      return true;
    }

    if (/drukste robot|meeste storingen|welke robot heeft de meeste|meest bezochte robot/.test(text)) {
      if (snapshot.topRobot) {
        respondToVoiceAssistant(`De robot met de meeste meldingen is ${snapshot.topRobot.name} met ${snapshot.topRobot.count} meldingen.`, 'info');
      } else {
        respondToVoiceAssistant('Ik zie nog niet genoeg robotdata om daar een topantwoord op te geven.', 'info');
      }
      return true;
    }

    if (/drukste vestiging|meeste meldingen bij welke vestiging|welke vestiging is het drukst/.test(text)) {
      if (snapshot.topVestiging) {
        respondToVoiceAssistant(`De drukste vestiging is ${snapshot.topVestiging.name} met ${snapshot.topVestiging.count} meldingen.`, 'info');
      } else {
        respondToVoiceAssistant('Ik zie nog niet genoeg vestigingsdata om dat te bepalen.', 'info');
      }
      return true;
    }

    if (/gemiddeld.*(robot|duur|bezig|reistijd)|hoe lang.*robot|hoe lang zijn we gemiddeld bezig/.test(text)) {
      respondToVoiceAssistant(findRobotPerformanceInsight(text), 'info');
      return true;
    }

    if (/(zoek online|zoek op|zoek eens|google)\s+/.test(text)) {
      const query = text.replace(/^(zoek online|zoek op|zoek eens|google)\s+/, '').trim();
      if (query && openOnlineSearch(query)) {
        respondToVoiceAssistant(`Ik heb online zoekresultaten geopend voor ${query}.`, 'info', { speak: false });
      } else {
        respondToVoiceAssistant('Ik kon de online zoekopdracht niet openen.', 'warning');
      }
      return true;
    }

    if (/versie|app versie|welke versie/.test(text)) {
      respondToVoiceAssistant(`Je gebruikt app versie ${nativeAndroidVersion || currentAndroidVersion}.`, 'info');
      return true;
    }

    if (/ben ik online|internet|verbinding/.test(text)) {
      respondToVoiceAssistant(isOnline ? 'De app is online en verbonden.' : 'De app is nu offline.', isOnline ? 'success' : 'warning');
      return true;
    }

    if (/update beschikbaar|is er een update|nieuwe versie/.test(text)) {
      if (androidUpdateInfo?.version) {
        respondToVoiceAssistant(`Ja, versie ${androidUpdateInfo.version} staat klaar. Je kunt die via je profiel downloaden of installeren.`, 'info');
      } else {
        respondToVoiceAssistant('Er is op dit moment geen nieuwe update gemeld.', 'info');
      }
      return true;
    }

    if (/(download|downloaden).*(apk|update)|apk downloaden/.test(text)) {
      if (typeof downloadLatestAndroidApk === 'function') {
        respondToVoiceAssistant('Ik start de APK-download voor je.', 'info');
        await downloadLatestAndroidApk();
      } else {
        respondToVoiceAssistant('APK downloaden is hier nu niet beschikbaar.', 'warning');
      }
      return true;
    }

    if (/(controleer|check).*(update)|update controleren/.test(text)) {
      if (typeof fetchAndroidUpdate === 'function') {
        respondToVoiceAssistant('Ik controleer nu op een update.', 'info');
        await fetchAndroidUpdate();
      } else {
        respondToVoiceAssistant('Updatecontrole is nu niet beschikbaar.', 'warning');
      }
      return true;
    }

    if (/notificaties uit|zet notificaties uit/.test(text)) {
      if (notificationsEnabled && typeof toggleNotifications === 'function') {
        await toggleNotifications();
      }
      respondToVoiceAssistant('Ik heb de notificaties uitgeschakeld.', 'info');
      return true;
    }

    if (/notificaties aan|zet notificaties aan/.test(text)) {
      if (!notificationsEnabled && typeof toggleNotifications === 'function') {
        await toggleNotifications();
      }
      respondToVoiceAssistant('Ik heb de notificaties ingeschakeld.', 'success');
      return true;
    }

    if (/donkere modus|dark mode|zet donker thema|nachtmodus/.test(text)) {
      if (!darkMode && typeof toggleDarkMode === 'function') toggleDarkMode();
      respondToVoiceAssistant('Donkere modus is ingeschakeld.', 'info');
      return true;
    }

    if (/lichte modus|licht thema|zet licht thema/.test(text)) {
      if (darkMode && typeof toggleDarkMode === 'function') toggleDarkMode();
      respondToVoiceAssistant('Lichte modus is ingeschakeld.', 'info');
      return true;
    }

    if (/hoeveel meldingen|aantal meldingen|hoeveel open meldingen/.test(text)) {
      respondToVoiceAssistant(`Ik zie ${snapshot.total} meldingen in totaal en ${snapshot.visible} in het huidige overzicht. Meldingen hebben geen workflowstatus.`, 'info');
      return true;
    }

    if (/hoeveel afgerond|hoeveel gesloten|hoeveel klaar|hoeveel in behandeling|status meldingen/.test(text)) {
      respondToVoiceAssistant('Meldingen hebben geen workflowstatus; ik kan geen aantallen open, afgerond of in behandeling geven.', 'info');
      return true;
    }

    if (/voorraadstatus|onderdelen zonder voorraad|lage voorraad|hoeveel onderdelen/.test(text)) {
      respondToVoiceAssistant(`Ik zie ${onderdelen.length} onderdelen. Daarvan zijn er ${outOfStockCount} zonder voorraad en ${lowStockCount} met lage voorraad.`, 'info');
      return true;
    }

    if (/hoeveel notificaties|hoeveel waarschuwingen/.test(text)) {
      respondToVoiceAssistant(`Je hebt nu ${notificationCount || 0} open notificaties of waarschuwingen.`, 'info');
      return true;
    }

    if (/advies|wat raad je aan|waar moet ik op letten|wat moet ik nu doen/.test(text)) {
      const advice = [];
      if (snapshot.urgentCount > 0) advice.push(`Ik zie ${snapshot.urgentCount} urgente meldingen die prioriteit verdienen.`);
      if (snapshot.outOfStockCount > 0) advice.push(`Daarnaast zijn ${snapshot.outOfStockCount} onderdelen volledig uit voorraad.`);
      if (snapshot.lowStockCount > 0) advice.push(`${snapshot.lowStockCount} onderdelen zitten laag in voorraad.`);
      if (!advice.length) advice.push('De situatie oogt stabiel. Ik zou vooral open meldingen en de dagplanning blijven volgen.');
      respondToVoiceAssistant(advice.join(' '), 'info');
      return true;
    }

    if (/(robot|krinkels|maaier|storing|onderhoud)/.test(text)) {
      respondToVoiceAssistant(getOperationalBriefing(), 'info');
      return true;
    }

    return false;
  };

  const startVoiceRecognition = async () => {
    if (!voiceSystem.voiceSupported) {
      showToast?.('❌ Spraakherkenning niet ondersteund', 'error');
      return;
    }

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        showToast?.('Spraakherkenning vereist een browser met microfoonondersteuning.', 'error');
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
    } catch (permError) {
      console.error('Microphone permission error:', permError);
      if (permError.name === 'NotAllowedError' || permError.name === 'SecurityError') {
        showToast?.('❌ Microfoon geweigerd. Geef deze site toestemming via het slotje in de adresbalk en probeer daarna opnieuw.', 'error');
      } else if (permError.name === 'NotFoundError') {
        showToast?.('❌ Geen microfoon gevonden', 'error');
      } else {
        showToast?.('❌ Microfoon fout: ' + permError.message, 'error');
      }
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.maxAlternatives = 3;
    recognition.lang = 'nl-NL';

    recognition.onstart = () => {
      setVoiceSystem((prev) => ({ ...prev, isListening: true }));
      showToast?.('🎤 Luisteren...', 'info');
    };

    recognition.onresult = async (event) => {
      const latestResult = event.results[event.results.length - 1];
      if (!latestResult || !latestResult[0]) return;

      const transcript = String(latestResult[0].transcript || '').trim();
      const confidence = Number(latestResult[0].confidence || 0);
      const isFinal = !!latestResult.isFinal;
      const wakeState = stripWakeWord(transcript);
      const effectiveTranscript = wakeState.text || transcript;
      const alternatives = Array.from(latestResult)
        .slice(0, 3)
        .map((alt) => ({ transcript: String(alt?.transcript || '').trim(), confidence: Number(alt?.confidence || 0) }))
        .filter((alt) => alt.transcript);

      setVoiceSystem((prev) => ({ ...prev, transcript, confidence }));
      if (!isFinal) return;

      const minConfidence = 0.4;
      if (confidence > 0 && confidence < minConfidence) {
        showToast?.(`⚠️ Onzekere spraak (${Math.round(confidence * 100)}%). Probeer opnieuw.`, 'warning');
        return;
      }

      const now = Date.now();
      const normalizedTranscript = normalizeVoiceText(effectiveTranscript);
      const last = lastVoiceExecutionRef.current;

      if (last.transcript === normalizedTranscript && (now - last.at) < 1800) {
        return;
      }

      if (pendingVoiceQuestionRef.current.type && now < pendingVoiceQuestionRef.current.expiresAt) {
        const pendingType = pendingVoiceQuestionRef.current.type;
        const resolvedRoute = pendingType === 'analytics_target'
          ? resolveAnalyticsFollowup(effectiveTranscript)
          : resolveAdminFollowup(effectiveTranscript);

        if (resolvedRoute) {
          pendingVoiceQuestionRef.current = { type: '', expiresAt: 0 };
          navigate?.(resolvedRoute);
          respondToVoiceAssistant('Begrepen, ik open de gevraagde pagina.', 'success');
          return;
        }

        const pendingPrompt = pendingType === 'admin_target'
          ? 'Ik heb het nog niet goed begrepen. Zeg bijvoorbeeld meldingen, robots of gebruikers.'
          : 'Ik heb het nog niet goed begrepen. Zeg bijvoorbeeld robots of onderdelen.';
        showToast?.('⚠️ Nog niet duidelijk. Probeer opnieuw.', 'warning');
        speakVoiceAssistant(pendingPrompt);
        return;
      }

      const handledQuestion = await handleVoiceQuestion(effectiveTranscript);
      if (handledQuestion) {
        lastVoiceExecutionRef.current = {
          intent: 'conversation',
          transcript: normalizedTranscript,
          at: now
        };
        updateVoiceLearning(effectiveTranscript, 'conversation');
        return;
      }

      const rankedCandidates = alternatives
        .map((alt) => {
          const intent = getVoiceIntent(stripWakeWord(alt.transcript).text);
          if (!intent) return null;
          return { intent, alt, totalScore: intent.score + Math.max(0, alt.confidence * 2) };
        })
        .filter(Boolean)
        .sort((a, b) => b.totalScore - a.totalScore);

      const intent = rankedCandidates[0]?.intent || getVoiceIntent(effectiveTranscript);
      if (!intent) {
        showToast?.('⚠️ Niet herkend. Vraag bijvoorbeeld naar het weer, je vestiging of zeg: ga naar analytics.', 'warning');
        speakVoiceAssistant('Ik begreep dat nog niet. Je kunt mij ook vragen hoe het weer is, hoeveel meldingen er zijn of om een pagina te openen.');
        return;
      }

      lastVoiceExecutionRef.current = {
        intent: intent.key,
        transcript: normalizedTranscript,
        at: now
      };

      if (intent.key === 'analytics' && !/robots?|onderdelen?/.test(normalizedTranscript)) {
        pendingVoiceQuestionRef.current = { type: 'analytics_target', expiresAt: now + 15000 };
        respondToVoiceAssistant('Wil je analytics voor robots of onderdelen?', 'info');
        return;
      }

      if (intent.key === 'administratie' && !/melding|robot|onderdeel|gebruiker|vestiging|garantie|documentatie|changelog|audit|portal|link|intranet|yamabiko|belrobotics|checklist|winterbeurt/.test(normalizedTranscript)) {
        pendingVoiceQuestionRef.current = { type: 'admin_target', expiresAt: now + 15000 };
        respondToVoiceAssistant('Welke administratie sectie bedoel je? Bijvoorbeeld meldingen, robots of onderdelen.', 'info');
        return;
      }

      navigate?.(intent.route);
      updateVoiceLearning(effectiveTranscript, intent.key);
      respondToVoiceAssistant(intent.toast.replace('✅', '').trim(), 'success');
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      setVoiceSystem((prev) => ({ ...prev, isListening: false }));

      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        showToast?.('❌ Microfoon geblokkeerd. Geef toestemming via het slotje in de adresbalk en probeer daarna opnieuw.', 'error');
      } else if (event.error === 'no-speech') {
        showToast?.('⚠️ Geen spraak gedetecteerd', 'warning');
      } else if (event.error === 'aborted') {
        showToast?.('ℹ️ Spraakherkenning gestopt', 'info');
      } else {
        showToast?.(`❌ Spraakherkenning fout: ${event.error}`, 'error');
      }
    };

    recognition.onend = () => {
      setVoiceSystem((prev) => ({ ...prev, isListening: false }));
    };

    try {
      recognition.start();
      window.currentRecognition = recognition;
    } catch (error) {
      console.error('Failed to start recognition:', error);
      setVoiceSystem((prev) => ({ ...prev, isListening: false }));
      showToast?.('❌ Kan spraakherkenning niet starten', 'error');
    }
  };

  const stopVoiceRecognition = () => {
    if (window.currentRecognition) {
      window.currentRecognition.stop();
      setVoiceSystem((prev) => ({ ...prev, isListening: false }));
      showToast?.('🛑 Gestopt met luisteren', 'info');
    }
  };

  return {
    voiceSystem,
    startVoiceRecognition,
    stopVoiceRecognition,
  };
}
